// src/components/coverpage/CoverPage.js
import React, { useMemo, useState } from "react";
import "./Coverpage.css";
import { generateWbrPack } from "wbr/utils/generateWbrPack";

export default function CoverPage({
  theme,
  functionId,
  week = "N/A",
  year = "N/A",
  functionConfig,
}) {
  const details = functionConfig?.function_details || {};
  const pocData = functionConfig?.function_poc || {};

  // ─── WBR Pack Download State ───
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState("");

  const handleDownloadWbrPack = async () => {
    setDownloading(true);
    setDownloadProgress("Starting...");
    try {
      await generateWbrPack({
        functionId,
        year,
        week,
        functionConfig,
        onProgress: setDownloadProgress,
      });
      setDownloadProgress("Done!");
    } catch (err) {
      console.error("WBR Pack generation failed:", err);
      alert(`PDF generation failed: ${err.message}\n\nCheck browser console for details.`);
      setDownloadProgress("");
    } finally {
      setDownloading(false);
      setTimeout(() => setDownloadProgress(""), 3000);
    }
  };

  const meetingDetails = useMemo(
    () => ({
      meetingLink: `${details.meeting_link}`,
      meetingType: "Weekly Business Review",
      period: `Week ${details.week ?? week}, ${year}`,
      status: "Internal Use Only",
      dateRange: details.daterange?.display || "N/A",
      meetingCoordination:
        details.meeting_coordination?.login || "Not assigned",
      meetingFacilitator:
        details.meeting_facilitator?.login || "Not assigned",
      meetingNoteTaker:
        details.meeting_note_taker?.login || "Not assigned",
    }),
    [details, week, year]
  );

  // ─── POC table: 100% driven by function_poc from admin panel ───
  const tableData = useMemo(() => {
    if (!pocData || Object.keys(pocData).length === 0) return [];

    return Object.entries(pocData).map(([pocKey, pocEntry]) => ({
      key: pocKey,
      programOrg: pocEntry.label || pocKey,
      slideOwner: pocEntry.slide_poc || { primary: "—", secondary: "—" },
      wfmPoc: pocEntry.wfm_poc || { primary: "—", secondary: "—" },
      opsPoc: pocEntry.ops_poc || { primary: "—", secondary: "—" },
    }));
  }, [pocData]);

  // ─── Define all possible sub-columns ───
  const allSubColumns = useMemo(
    () => [
      { groupKey: "slideOwner", subKey: "primary", groupLabel: "Slide Owner", subLabel: "Primary" },
      { groupKey: "slideOwner", subKey: "secondary", groupLabel: "Slide Owner", subLabel: "Secondary" },
      { groupKey: "wfmPoc", subKey: "primary", groupLabel: "WFM POC", subLabel: "Primary" },
      { groupKey: "wfmPoc", subKey: "secondary", groupLabel: "WFM POC", subLabel: "Secondary" },
      { groupKey: "opsPoc", subKey: "primary", groupLabel: "OPS POC", subLabel: "Primary" },
      { groupKey: "opsPoc", subKey: "secondary", groupLabel: "OPS POC", subLabel: "Secondary" },
    ],
    []
  );

  // ─── Helper: check if a value is blank/empty/dash ───
  const isBlank = (val) => !val || val === "—" || val === "-" || val.trim() === "";

  // ─── Filter out sub-columns where ALL rows are blank ───
  const visibleSubColumns = useMemo(() => {
    if (tableData.length === 0) return [];

    return allSubColumns.filter((col) =>
      tableData.some((row) => {
        const cellValue = row[col.groupKey]?.[col.subKey];
        return !isBlank(cellValue);
      })
    );
  }, [tableData, allSubColumns]);

  // ─── Build grouped header structure from visible sub-columns ───
  const groupedHeaders = useMemo(() => {
    const groups = [];
    visibleSubColumns.forEach((col) => {
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.groupLabel === col.groupLabel) {
        lastGroup.colSpan += 1;
        lastGroup.subColumns.push(col);
      } else {
        groups.push({
          groupKey: col.groupKey,
          groupLabel: col.groupLabel,
          colSpan: 1,
          subColumns: [col],
        });
      }
    });
    return groups;
  }, [visibleSubColumns]);

  // ─── Helper: turn "(loginid)" into a clickable phonetool link ───
  const renderWithPhonetool = (text) => {
    if (!text || typeof text !== "string") return text;
    // Match "Name (loginid)" pattern
    const regex = /^(.*?)\((\w+)\)(.*)$/;
    const match = text.match(regex);
    if (!match) return text;
    const [, before, loginId, after] = match;
    return (
      <>
        {before}(
        <a
          href={`https://phonetool.amazon.com/users/${loginId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="cp-link cp-link--login"
        >
          {loginId}
        </a>
        ){after}
      </>
    );
  };

  // ─── Helper: turn a plain login ID into a clickable phonetool link ───
  const renderLoginAsLink = (text) => {
    if (!text || typeof text !== "string" || text === "—" || text === "-" || text.trim() === "") return text;
    // If it contains parentheses, use the other helper
    if (text.includes("(")) return renderWithPhonetool(text);
    // Plain login ID: single word, alphanumeric (no spaces)
    const trimmed = text.trim();
    if (/^[a-zA-Z0-9]+$/.test(trimmed)) {
      return (
        <a
          href={`https://phonetool.amazon.com/users/${trimmed}`}
          target="_blank"
          rel="noopener noreferrer"
          className="cp-link cp-link--login"
        >
          {trimmed}
        </a>
      );
    }
    return text;
  };

  const formatDisplayKey = (key) => {
    if (!key) return "";
    if (key === "meetingLink") return "Meeting Link";
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/([a-zA-Z])([A-Z][a-z])/g, "$1 $2");
  };

  const formatDisplayValue = (key, value) => {
    if (!value || value === "Not assigned") {
      return <span className="cp-not-assigned">Not assigned</span>;
    }
    if (
      key === "meetingLink" &&
      typeof value === "string" &&
      value.startsWith("http")
    ) {
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="cp-link"
        >
          Join Here
        </a>
      );
    }
    // For meeting coordination/facilitator/note taker fields, render phonetool links
    if (
      (key === "meetingCoordination" ||
        key === "meetingFacilitator" ||
        key === "meetingNoteTaker") &&
      typeof value === "string"
    ) {
      return renderWithPhonetool(value);
    }
    return value;
  };

  const TableComponent = ({ data }) => {
    if (!data || data.length === 0 || visibleSubColumns.length === 0) {
      return null;
    }

    // Check if we need a two-row header (any group has more than 1 sub-column)
    const needsGroupRow = groupedHeaders.some((g) => g.colSpan > 1);

    return (
      <div className="cp-table-container">
        <div className="cp-table-responsive">
          <table className="cp-table">
            <thead className="cp-thead">
              {needsGroupRow ? (
                <>
                  {/* Row 1: Group headers + "Program / ORG" spanning 2 rows */}
                  <tr>
                    <th rowSpan="2" className="cp-th">
                      Program / ORG
                    </th>
                    {groupedHeaders.map((group) => (
                      <th
                        key={group.groupKey + group.groupLabel}
                        colSpan={group.colSpan}
                        className="cp-th"
                      >
                        {group.groupLabel}
                      </th>
                    ))}
                  </tr>
                  {/* Row 2: Sub-column headers (Primary / Secondary) */}
                  <tr>
                    {visibleSubColumns.map((col) => (
                      <th
                        key={`${col.groupKey}-${col.subKey}`}
                        className="cp-th"
                      >
                        {col.subLabel}
                      </th>
                    ))}
                  </tr>
                </>
              ) : (
                /* Single header row when every group has exactly 1 sub-column */
                <tr>
                  <th className="cp-th">Program / ORG</th>
                  {groupedHeaders.map((group) => (
                    <th
                      key={group.groupKey + group.groupLabel}
                      className="cp-th"
                    >
                      {/* Show "Group - Sub" for clarity, e.g. "Slide Owner - Primary" */}
                      {group.groupLabel} - {group.subColumns[0].subLabel}
                    </th>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.key} className="cp-table-row">
                  <td className="cp-td-org">{row.programOrg}</td>
                  {visibleSubColumns.map((col) => {
                    const cellValue = row[col.groupKey]?.[col.subKey] || "—";
                    return (
                      <td key={`${row.key}-${col.groupKey}-${col.subKey}`}>
                        {renderLoginAsLink(cellValue)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="cp-container">
      {/* ───── Card One: Main Cover ───── */}
      <div className="cp-card-one">
        <div className="cp-card">
          <div className="cp-header-banner">
            <div className="cp-header-content">
              <div className="cp-header-left">
                <h1 className="cp-header-title">
                  WBR Week {details.week ?? week} {year}
                </h1>
                <p className="cp-header-subtitle">
                  {details.daterange?.display || "Date range not available"}
                </p>
              </div>
              <div className="cp-header-right">
                <h2 className="cp-header-org">
                  {details.full_name ||
                    "Verification Risk Management & Operations"}
                </h2>
              </div>
            </div>
          </div>

          <h2 className="cp-section-title">Weekly Business Review</h2>

          <div className="cp-classification">
            <span className="cp-classification-badge">
              Document Classification: Amazon Confidential
            </span>
          </div>

          <div className="cp-meeting-details">
            {Object.entries(meetingDetails).map(([key, value]) => (
              <div key={key} className="cp-detail-row">
                <span className="cp-detail-key">
                  {formatDisplayKey(key)}:
                </span>
                <span className="cp-detail-value">
                  {formatDisplayValue(key, value)}
                </span>
              </div>
            ))}
          </div>

          {/* ─── Download WBR Pack Button ─── */}
          <div style={{ padding: "16px 24px", borderTop: "1px solid #e8e8e8", display: "flex", alignItems: "center", gap: "12px" }}>
            <button
              onClick={handleDownloadWbrPack}
              disabled={downloading || week === "N/A"}
              style={{
                padding: "10px 20px",
                fontSize: "13px",
                fontWeight: 600,
                background: downloading ? "#6b7280" : "#232f3e",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                cursor: downloading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "background 0.2s",
              }}
            >
              {downloading ? (
                <>⏳ {downloadProgress}</>
              ) : (
                <>📥 Download Full WBR Pack</>
              )}
            </button>
            {!downloading && (
              <span style={{ fontSize: "12px", color: "#6b7280" }}>
                Combines cover page, business updates, action tracker, and all reports into one PDF
              </span>
            )}
          </div>

          <div className="cp-contact">
            <p className="cp-contact-header">Contact us</p>
            <p className="cp-contact-text">
              For inquiries, please contact us at:{" "}
              <a href="mailto:swots-cia@amazon.com" className="cp-link">
                {details.function_team || "Team Not Available"}
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* ───── Card Two: POC Table (only if data exists) ───── */}
      {tableData.length > 0 && (
        <div className="cp-card-two">
          <div className="cp-card">
            <h4 className="cp-section-title">
              {details.label || "VRMO Ops WBR"} Program POC
            </h4>
            <TableComponent data={tableData} />
          </div>
        </div>
      )}

      {/* ───── Card Three: Footer ───── */}
      <div className="cp-card-three">
        <div className="cp-footer-bar">
          Amazon Confidential | Internal Use Only
        </div>
      </div>
    </div>
  );
}
